import { Button } from "primereact/button";
import { useEffect,useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useTranslation } from "react-i18next";
import {POSModel} from '../../models/orders/order-list-model';
import orderServices from '../../services/orders/orderservice';
import '../../assests/css/common/responsive-ui.css'
import IncomingRMRequestsDialog from "./Incoming-RawMaterial-Request";
import posService from "../../services/pos/pos.service";
import { Badge } from "primereact/badge";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import AddRawMaterialstopos from "./add-rawMaterial-pos";

interface SearchPanelProps {
  onSearch: (query: string) => void;
  setLoading: (loading: boolean) => void;
  onPOSChange: (posId: number) => void;
  onAddSuccess: (posId: number) => void;
}

const RawMaterialInventorySearch: React.FC<SearchPanelProps> = ({
  onSearch,
  setLoading,
  onPOSChange,
  onAddSuccess
}) => {
  const { t } = useTranslation();
  const [keywordsearch, setkeywordsearch] = useState<string>("");
  const [pointOfSale, setPointOfSale] = useState<any>();
  const [posData, setPOSData] = useState<POSModel | any>();
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showRequestsDialog, setShowRequestsDialog] = useState(false);
  const [requestCount, setRequestCount] = useState<number>(0);
  const [inwardrequestCount, setinwardRequestCount] = useState<number>(0);
  const [isPOSSelected, setIsPOSSelected] = useState(false);
  const totalRequestCount = requestCount + inwardrequestCount;
  const toast = useRef<Toast>(null);

  // Alias function for Manufacturing Units
  const aliasMU = (id: number, name?: string) => {
    if (id === 3) return "Unit-1-KR"; // K R Nagar
    if (id === 4) return "Unit-2-MT"; // Magadi Taluk
    const up = (name ?? "").toUpperCase().replace(/\s+/g, " ").trim();
    if (/\bK\sR\sNAGAR\b/.test(up) || up.includes("KR NAGAR")) return "Unit-1-KR";
    if (up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
    return name ?? "";
  };

  const validatePOS = (): boolean => {
    const isValid = !!pointOfSale;
    setIsPOSSelected(isValid);
    return isValid;
  };

  const handleSearch = () => {
    if (!validatePOS()) {
      showMessage("Error", "Please select a POS", "warn");
      return;
    }
    setLoading(true)
    onSearch(keywordsearch);
  };

  const resetSearchFields = () => {
    setkeywordsearch("");
    onSearch("");
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const getALLPOS = async () => {
    try {
      const response: POSModel[] = await RawMaterialService.GetManufacrturingunitlist();
      setPOSData(response);
      // Auto-select if there's only one POS
      if (response.length === 1) {
        const singlePOS = response[0].posID;
        setPointOfSale(singlePOS);
        onPOSChange(singlePOS);
        refreshRequests(singlePOS);
        setIsPOSSelected(true);
      }
    } catch (error) {
      showMessage("Error", "Failed to fetch POS data", "error");
    }
  };

  useEffect(()=>{
    getALLPOS();
  },[])

  const handlePOSChange = async (e: DropdownChangeEvent) => {
    const selectedPosId = e.value;
    setPointOfSale(selectedPosId);
    onPOSChange(selectedPosId);
    await refreshRequests(selectedPosId);
  };

  const handleOnSuccess = () => {
    setShowAddProductModal(false);
    showMessage("Add Raw Material", "Raw Material added successfully", "success");
    onAddSuccess(pointOfSale);
  };

  const handleSendRequest = async (request: any) => {
    try {
      setLoading(true);
      const sendData = {
        requestId: request.id,
        rawMaterialId: request.rawMaterialId,
        posId: request.requestToPos,
        toPosId: request.requestFromPos,
        vendorId: request.vendorId,
        invoiceNumber: request.invoiceNumber,
        quantity: request.quantity,
        note: request.note,
        documentDetails:request.documentDetails || "",
        rawMaterialPurchaseId: request.rawMaterialPurchaseId
      };
      
      const response = await RawMaterialService.updateRawMaterialRequestSend(sendData);
      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
        onAddSuccess(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to send RawMaterial", "error");
      }
    } catch (error) {
      console.error("Error sending RawMaterial", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (request: any) => {
    try {
      setLoading(true);
      const rejectData = {
        requestId: request.id,
        rawMaterialId: request.rawMaterialId,
        posId: request.requestToPos,
        toPosId: request.requestFromPos,
        vendorId: request.vendorId,
        invoiceNumber: request.invoiceNumber,
        quantity: request.quantity,
        note: request.note,
        rawMaterialPurchaseId: request.rawMaterialPurchaseId
      };
      
      const response = await RawMaterialService.updateRawMaterialRequestReject(rejectData);

      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false); 
        await refreshRequests(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to send RawMaterial", "error");
      }
    } catch (error) {
      console.error("Error sending RawMaterial", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInwardSendRequest = async (request: any) => {
    try {
      setLoading(true);
      const sendData = {
        requestId: request.id,
        rawMaterialId: request.rawMaterialId,
        posId: request.requestToPos,
        toPosId: request.requestFromPos,
        quantity: request.quantity,
        vendorId: request.vendorId,
        invoiceNumber: request.invoiceNumber,
        note: request.note || "",
        documentDetails: request.documentDetails || "",
        rawMaterialPurchaseId: request.rawMaterialPurchaseId
      };
      
      const response = await RawMaterialService.updateRawMaterialInwardAccept(sendData);
      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
        onAddSuccess(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to accept request", "error");
      }
    } catch (error) {
      console.error("Error accepting inward request", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInwardRejectRequest = async (request: any) => {
    try {
      setLoading(true);
      const rejectData = {
        requestId: request.id,
        productId: request.productId,
        posId: request.requestToPOS,
        toPosId: request.requestFromPOS,
        quantity: request.quantity,
        note: request.note || ""
      };
      
      // Uncomment when API is ready
      // const response = await posService.UpdatePosinwardReject(rejectData);
      // if (response?.isSuccess) {
      //   showMessage("Success", response.message, "success");
      //   setShowRequestsDialog(false);
      //   await refreshRequests(pointOfSale);
      // } else {
      //   showMessage("Failed", response?.message || "Failed to reject request", "error");
      // }
    } catch (error) {
      console.error("Error rejecting inward request", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const refreshRequests = async (selectedPosId: number) => {
    try {
      const response = await RawMaterialService.GetRawMaterialOutwardRequests(selectedPosId);
      if (response) {
        setRequestCount(response.length);
      } else {
        setRequestCount(0);
      }
      const response1 = await RawMaterialService.GetRawMaterialInwardRequests(selectedPosId);
      setinwardRequestCount(response1?.length || 0);
    } catch (error) {
      console.error("Failed to fetch requests count", error);
      setRequestCount(0);
      setinwardRequestCount(0);
    }
  };

  return (
    <div className="col-12" style={{ width: "100%", padding: "0px", marginBottom: "0.5rem" }}>
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div className="grid align-items-center">
          
          {/* POS Dropdown */}
          <div className="col-12 md:col-2">
            <Dropdown
              options={posData}
              optionLabel="posCode"
              optionValue="posID"
              value={pointOfSale || null}
              onChange={(e) => {
                handlePOSChange(e);
                setIsPOSSelected(true);
              }}
              placeholder="Select Manufacturing Unit"
              required
              className={`w-full ${!isPOSSelected && pointOfSale === "" ? "p-invalid" : ""}`}
              disabled={posData?.length === 1}
              itemTemplate={(option: POSModel) => {
                const displayName = aliasMU(option.posID, option.posCode || option.address2);
                return displayName;
              }}
              valueTemplate={(option: POSModel, props: any) => {
                if (option) {
                  const displayName = aliasMU(option.posID, option.posCode || option.address2);
                  return displayName;
                }
                return props.placeholder;
              }}
            />
            {!isPOSSelected && pointOfSale === "" && (
              <small className="p-error">POS is required</small>
            )}
          </div>

          {/* Search Input */}
          <div className="col-12 md:col-3">
            <div className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={keywordsearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setkeywordsearch(value);
                  onSearch(value);
                }}
                placeholder={t("Raw material search")}
                className="w-full"
              />
            </div>
          </div>

          {/* Filter Button */}
          <div className="col-6 md:col-1">
            <Button
              label="Filter"
              onClick={handleSearch}
              severity="success"
              className="w-full"
            />
          </div>

          {/* Reset Button */}
          <div className="col-6 md:col-1">
            <Button
              label="Reset"
              onClick={resetSearchFields}
              severity="secondary"
              className="w-full"
              text
              raised
            />
          </div>

          {/* Add Product + Bell Button */}
          <div className="col-12 md:col-3 ml-auto flex justify-content-end gap-2 mt-2 md:mt-0">
            <Button
              label="Add Raw Materials"
              className="bg-blue-700 text-white border-round"
              onClick={() => setShowAddProductModal(true)}
              text
              raised
              disabled={!pointOfSale}
            />
            
            <div style={{ position: "relative", display: "inline-block" }}>
              <Button
                type="button"
                icon="pi pi-bell"
                severity="secondary"
                disabled={!pointOfSale}
                onClick={() => setShowRequestsDialog(true)}
              />
              {totalRequestCount > 0 && (
                <Badge
                  value={totalRequestCount}
                  severity="danger"
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px"
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Dialogs */}
        <Dialog
          visible={showAddProductModal}
          className="quickview w-30"
          breakpoints={{ "960px": "75vw", "641px": "90vw" }}
          modal
          onHide={() => setShowAddProductModal(false)}
        >
          <AddRawMaterialstopos posId={pointOfSale} onSuccess={handleOnSuccess} />
        </Dialog>

        <IncomingRMRequestsDialog
          visible={showRequestsDialog}
          onHide={() => setShowRequestsDialog(false)}
          posId={pointOfSale}
          onSendRequest={handleSendRequest}
          onRejectRequest={handleRejectRequest}
          onSendinwardRequest={handleInwardSendRequest}
          onRejectinwardRequest={handleInwardRejectRequest}
          setLoading={setLoading}
          outwardCount={requestCount}
          inwardCount={inwardrequestCount}
        />
      </div>
    </div>
  );
};

export default RawMaterialInventorySearch;